import React, { useState } from 'react';
import {
  Lightbulb,
  MapPin,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import type { LightPoint, WizBulb, ZoneId } from '../../types/ambilight';

import { useAmbilightContext } from '../../context/AmbilightContext';

export interface LightManagerModalProps {
  bulbs?: WizBulb[];
  points?: LightPoint[];
  isOpen?: boolean;
  onClose?: () => void;
  onAddBulb?: (name: string, ip: string) => Promise<boolean>;
  onRemoveBulb?: (bulbId: string) => void;
  onSetPrimaryBulb?: (bulbId: string) => void;
  onTogglePointAssignment?: (bulbId: string, pointId: ZoneId) => void;
  onUpdateMaxBrightness?: (bulbId: string, maxBrightness: number) => void;
}

export const LightManagerModal: React.FC<LightManagerModalProps> = (props) => {
  const context = useAmbilightContext();

  const bulbs = props.bulbs ?? context.bulbs;
  const points = props.points ?? context.points;
  const isOpen = props.isOpen ?? context.isLightManagerOpen;
  const onClose = props.onClose ?? (() => context.setIsLightManagerOpen(false));
  const onAddBulb = props.onAddBulb ?? context.addBulb;
  const onRemoveBulb = props.onRemoveBulb ?? context.removeBulb;
  const onSetPrimaryBulb = props.onSetPrimaryBulb ?? context.setPrimaryBulb;
  const onTogglePointAssignment = props.onTogglePointAssignment ?? context.togglePointAssignment;
  const onUpdateMaxBrightness = props.onUpdateMaxBrightness ?? context.updateBulbMaxBrightness;
  const [newIp, setNewIp] = useState('');
  const [newName, setNewName] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIp.trim()) return;

    setIsTesting(true);
    setTestResult('Comprobando respuesta UDP del foco...');
    const name = newName.trim() || `Foco WiZ ${bulbs.length + 1}`;
    const success = await onAddBulb(name, newIp.trim());
    setIsTesting(false);

    if (success) {
      setTestResult('✅ Foco añadido y verificado');
      setNewIp('');
      setNewName('');
      setTimeout(() => setTestResult(null), 2500);
    } else {
      setTestResult('⚠️ Añadido (sin respuesta UDP inmediata, verifica la IP)');
      setTimeout(() => setTestResult(null), 3000);
    }
  };

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop select-none"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl rounded-2xl modal-card p-6 text-white shadow-2xl"
        style={{
          backgroundColor: '#0f1422',
          backgroundImage: 'linear-gradient(180deg, #141b2d 0%, #0d121f 100%)',
        }}
      >
        {/* Encabezado */}
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">
                Administrador de Luces WiZ (Soporte Multi-Foco)
              </h3>
              <p className="text-xs text-white/50">
                Gestiona tus focos inteligentes y mapea qué zonas iluminan
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Lista de Focos Registrados */}
        <div className="my-5 space-y-3.5 max-h-[360px] overflow-y-auto pr-1">
          {bulbs.map((bulb) => (
            <div
              key={bulb.id}
              className={`p-4 rounded-xl border transition-all ${
                bulb.isPrimary
                  ? 'bg-purple-950/20 border-purple-500/40 shadow-sm shadow-purple-500/10'
                  : 'bg-white/5 border-white/10'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      bulb.connected
                        ? 'bg-emerald-400 shadow-sm shadow-emerald-400'
                        : 'bg-rose-400/80'
                    }`}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-white">{bulb.name}</span>
                      {bulb.isPrimary && (
                        <span className="px-1.5 py-0.2 rounded text-[10px] font-mono bg-purple-500/20 text-purple-300 border border-purple-500/30">
                          PRINCIPAL
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-xs text-cyan-300">{bulb.ip}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {!bulb.isPrimary && (
                    <button
                      onClick={() => onSetPrimaryBulb(bulb.id)}
                      className="px-2.5 py-1 rounded-lg text-xs bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 transition-colors"
                    >
                      Hacer Principal
                    </button>
                  )}
                  {bulbs.length > 1 && (
                    <button
                      onClick={() => onRemoveBulb(bulb.id)}
                      className="p-1.5 rounded-lg text-white/40 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                      title="Eliminar foco"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Slider Brillo Máximo por foco */}
              <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
                <span className="text-white/60">Brillo Máximo:</span>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={bulb.maxBrightness}
                    onChange={(e) => onUpdateMaxBrightness(bulb.id, Number(e.target.value))}
                    className="w-24 h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-400"
                  />
                  <span className="font-mono text-purple-300 w-8 text-right">
                    {bulb.maxBrightness}%
                  </span>
                </div>
              </div>

              {/* Ubicación física única del foco en la pantalla */}
              <div className="mt-3.5 pt-3 border-t border-white/10">
                {(() => {
                  const assignedPoint = points.find((p) => p.assignedBulbId === bulb.id);
                  return (
                    <>
                      <div className="flex items-center justify-between mb-2 text-xs">
                        <div className="flex items-center gap-1.5 text-white/80 font-medium">
                          <MapPin className="w-3.5 h-3.5 text-cyan-400" />
                          <span>Ubicación física del foco:</span>
                        </div>
                        {assignedPoint ? (
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-cyan-300 bg-cyan-950/60 px-2 py-0.5 rounded text-[11px] border border-cyan-800/40">
                              {assignedPoint.label} ({assignedPoint.shortLabel})
                            </span>
                            <button
                              type="button"
                              onClick={() => context.assignBulbToPoint(assignedPoint.id, null)}
                              className="text-white/40 hover:text-rose-300 text-[10px] underline cursor-pointer ml-1"
                              title="Desasignar este foco de la pantalla"
                            >
                              Desasignar
                            </button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-amber-300/80 font-mono bg-amber-950/40 px-2 py-0.5 rounded border border-amber-800/30">
                            Sin asignar (En espera)
                          </span>
                        )}
                      </div>

                      {/* Botones de selección de posición 1:1 */}
                      <div className="grid grid-cols-9 gap-1.5">
                        {points.map((pt) => {
                          const isHere = pt.assignedBulbId === bulb.id;
                          const otherBulb = !isHere && pt.assignedBulbId
                            ? bulbs.find((b) => b.id === pt.assignedBulbId)
                            : null;

                          return (
                            <button
                              key={pt.id}
                              type="button"
                              onClick={() => onTogglePointAssignment(bulb.id, pt.id)}
                              className={`flex flex-col items-center justify-center py-2 px-1 rounded-lg text-xs font-mono border transition-all cursor-pointer ${
                                isHere
                                  ? 'bg-purple-600/40 border-purple-400 text-white font-bold shadow-md shadow-purple-500/30 ring-2 ring-purple-400/60 scale-[1.02]'
                                  : otherBulb
                                  ? 'bg-white/[0.03] border-white/10 text-white/35 hover:border-amber-400/40 hover:text-white/60'
                                  : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white hover:border-white/20'
                              }`}
                              title={`${pt.label} (${pt.shortLabel}): ${
                                isHere
                                  ? '✓ Ubicado aquí'
                                  : otherBulb
                                  ? `Ocupado por: ${otherBulb.name} (clic para mover aquí)`
                                  : 'Disponible (0 focos) — clic para ubicar aquí'
                              }`}
                            >
                              <span className="text-[11px] font-bold">{pt.shortLabel}</span>
                              <span className="text-[9px] mt-0.5 truncate max-w-full">
                                {isHere ? '✓ Aquí' : otherBulb ? otherBulb.name.slice(0, 4) : '0'}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-white/50 italic mb-4">
          💡 En el mundo real, cada lámpara inteligente ocupa una única posición alrededor de tu pantalla. Si un punto no tiene foco asignado, muestra <strong>0</strong>.
        </p>

        {/* Formulario Añadir Foco WiZ */}
        <form
          onSubmit={handleAdd}
          className="p-3.5 rounded-xl bg-white/5 border border-white/10 space-y-3"
        >
          <div className="text-xs font-semibold text-white flex items-center gap-2">
            <Plus className="w-3.5 h-3.5 text-cyan-400" />
            <span>Añadir Nuevo Foco WiZ en tu Red</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Nombre (ej: Lámpara Escritorio)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-black/40 border border-white/15 text-xs text-white placeholder-white/40 outline-none focus:border-purple-400"
            />
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="IP (ej: 192.168.100.128)"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                className="flex-1 px-3 py-1.5 rounded-lg bg-black/40 border border-white/15 text-xs text-white placeholder-white/40 font-mono outline-none focus:border-purple-400"
              />
              <button
                type="submit"
                disabled={isTesting}
                className="px-4 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-medium text-xs flex items-center gap-1.5 transition-colors"
              >
                {isTesting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                <span>Añadir</span>
              </button>
            </div>
          </div>

          {testResult && (
            <p className="text-[11px] text-cyan-300 font-mono tracking-wide">{testResult}</p>
          )}
        </form>

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium text-xs transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
